import { Suspense, useContext, useEffect } from 'react';
import {
  hasLaborManagementAccess,
  hasLaborValidationViewAccess,
  Permissions,
  useScopeChecker,
} from 'src/rbac';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import DailySessionNotes from 'src/components/dailySessionNotes';
import CreateDocumentPrview from 'src/components/document-management/create-document-preview';
import EditDocumentPreview from 'src/components/document-management/edit-document-preview';
import PreviewDocument from 'src/components/document-management/preview-document';
import DocumentErrorPage from 'src/components/ErrorHandling/DocumentErrorPage';
import ErrorPage from 'src/components/ErrorHandling/Error-page';
import NotPermission from 'src/components/ErrorHandling/NotPermission';
import LaborReportAndApproval from 'src/components/labor-report-and-approval';
import AuthorizationEditDetails from 'src/components/therapyPlanning/authorizationEditDetails';
import AuthorizationDetails from 'src/components/therapyPlanning/autorizationDetails';
import MDSAssessment from 'src/components/therapyPlanning/mdsAssessment';
import TherapyPlanningEditDetails from 'src/components/therapyPlanning/therapyPlanningEditDetails';
import { Layout as AuthLayout } from 'src/layouts/auth/classic-layout';
import { Layout as DashboardLayout } from 'src/layouts/dashboard';
import { EmploymentLockoutModal } from 'src/layouts/dashboard/employment-lockout-modal';
import PostResetPassword from 'src/layouts/dashboard/post-reset-password/post-reset-password';
import LandingPage from 'src/pages/auth/landing-page';
import Login from 'src/pages/auth/login';
import LogoutLanding from 'src/pages/auth/logout-landing';
import PasswordResetComplete from 'src/pages/auth/password-reset-complete';
import ResetPassword from 'src/pages/auth/reset-password';
import EditFacilityStayPage from 'src/pages/details-page/edit-facility-stay';
import PatientEpisodeOfcare from 'src/pages/details-page/episodeOfCare';
import EpisodeOfCareViewDetails from 'src/pages/details-page/episodeOfCareDetails';
import PatientDetails from 'src/pages/details-page/patient-details';
import PatientScreening from 'src/pages/details-page/patientScreening';
import EditScreeningDocumentPreview from 'src/pages/details-page/view-add-new-screen-form/edit-screening-document';
import ScreeningPreviewDocument from 'src/pages/details-page/view-add-new-screen-form/screening-preview-document';
import ViewAddNewScreenForm from 'src/pages/details-page/view-add-new-screen-form/view-add-new-screen-form';
import AddPatientForm from 'src/pages/forms/addPatientForm';
import PatientUpload from 'src/pages/patient-upload';
import Patients from 'src/pages/patients';
import RecentUploadedfiles from 'src/pages/recent-uploaded-files';
import MyAvailability from 'src/pages/schedule/my-availability';
import LaborHourManagement from 'src/components/labor-hour-managment/labor-hour-managmenent';
import NpcLog from 'src/components/npc-log';
import PhysicianDashboard from 'src/components/physician/physician-dashboard';
import PhysicianDocumentsSign from 'src/components/physician/physician-documents-sign';
import MdsAssessmentUpload from 'src/components/therapyPlanning/mdsAssessmentUpload/mds-assessment-upload';
import TherapyPlanningDetails_2_0 from 'src/components/therapyPlanning_2.0/therapy-planing-details-2.0';
import TherapyPlanningInfo_2_0 from 'src/components/therapyPlanning_2.0/TherapyPlanningInfo-2.0';
import PhysicianSignDocument from 'src/pages/details-page/Physician-Signature';
import { default as TherapistScheduleNewUI } from 'src/pages/schedule/therapist/UI-Improvment/Therapist1';
import Dashboard from 'src/pages/therapist-dashboard';
import TherapistDashboardDocEdit from 'src/pages/therapist-dashboard/therapist-document/therapist-dashboarb-doc-edit';
import TherapistDashboardDocPreview from 'src/pages/therapist-dashboard/therapist-document/therapist-dashboard-doc-preview';
import { paths } from 'src/paths';
import {
  getAssignedFacilities,
  getTherapistProfileAction,
} from 'src/redux/actions/facilityActions';
import { getAllPatientsListResetAction } from 'src/redux/actions/patientAction';
import AddNewEpisodeOfCare from 'src/sections/dashboard/patients/addNewEpisodeOfCare';
import AddNewScreening from 'src/sections/dashboard/patients/addNewScreening';
import ScreeningDetails from 'src/sections/dashboard/patients/screeningDetails';
import DepartmentDashboard from 'src/sections/department-dashboard';
import { DeleteModal } from 'src/sections/modal/deleteModal';
import MonthDayAlert from 'src/sections/month-day-alert';
import MonthlyCloseoutAlert from 'src/sections/monthly-closeout-alert/monthly-closeout-alert';
import PreliminaryInvoiceReview from 'src/sections/preliminary-invoice-review';
import BackToPeriodRedirect from 'src/sections/preliminary-invoice-review/BackToPeriodRedirect';
import RedirectTab from 'src/utils/redirect-tab';
import AuthContext from './AuthContext';
import ErrorFallback from './ErroFallBack';
import PrivateRoute from './privateRoutes';
import PublicRoute from './publicRoutes';
import ServerErrorPage from './server-error';
import { fetchAndLogPublicIP } from 'src/layouts/components/utils-method';
import SignatureTemplatePreviewNew from 'src/components/document-management/signature-template-new';
import DashboardDocSignaturePreviewNew from 'src/pages/therapist-dashboard/therapist-document/therapist-dashboar-doc-preview-new';
import DocumentPreviewCarousel from 'src/pages/therapist-dashboard/therapist-document/therapist-bulk-signature/document-preview-carousel';
import MedicalCodeEditor from 'src/components/MedicalCodeEditor';
import DepartmentMetrics from 'src/sections/department-dashboard/DepartmentMetrics';
import MdsMetrics from 'src/sections/department-dashboard/MdsMetrics';
import DepartmentDownloads from 'src/sections/department-dashboard/DepartmentDownloads';
import MdsDashboard from 'src/pages/mds-dashboard';
import MdsDashboardPatient from 'src/pages/mds-dashboard-patient';
import PatientPayersInfoTab from 'src/pages/details-page/patient-details-payers-tab';
import AddEditPatientPayer from 'src/pages/details-page/add-edit-new-payer';
import MdsAssessments from 'src/components/mdsAssessment';

function Router() {
  const {
    index,
    dashboard,
    patients,
    patientDetails,
    screening,
    episodeOfCare,
    createPatient,
    schedule,
    editFacilityStay,
    addNewScreening,
    addNewEpisodeOfCare,
    editPatient,
    sreeningDetails,
    details,
    editEpisodeOfCare,
    therapyPlanning,
    deactivate,
    facilityStay,
    addFacilityStay,
    landingPage,
    landingPageLogout,
    dailySessionNotes,
    previewDocument,
    editDocument,
    createDocument,
    getSignaturePreview,
    monthDayAlert,
    laborManagement,
    redirectSupervisor,
    monthlyCloseoutAlert,
    preliminaryInvoiceReview,
    physicianSign,
    physicianDashboard,
    physicianDocumentsSign,
    departmentComplianceAlerts,
    departmentMetrics,
    mdsMetrics,
    departmentDownloads,
    payers,
    addNewPayer,
    mdsAssessments,
    createMdsAssessment,
  } = paths;

  const has = useScopeChecker();
  const getAllPatientRes = useSelector((state: any) => state.getAllPatientListReducer);
  const dispatch = useDispatch();
  const checkHost = window.location.hostname;
  const auth = useContext(AuthContext);
  const pathName = window.location.pathname;

  useEffect(() => {
    const isLoggedIn = auth.authenticated;
    fetchAndLogPublicIP().then((res) => {
      localStorage.setItem('ip-address', res);
    });
    if (!isLoggedIn) return;
    const checkPageRefresh = () => {
      const navigationEntries: PerformanceNavigationTiming[] = window.performance.getEntriesByType(
        'navigation'
      ) as PerformanceNavigationTiming[];

      if (navigationEntries.length > 0 && navigationEntries[0].type === 'reload') {
        dispatch(getTherapistProfileAction());
        dispatch(getAssignedFacilities());
      }
    };

    checkPageRefresh();
  }, []);

  useEffect(() => {
    const isLoggedIn = auth.authenticated;
    if (!isLoggedIn) return;
    if (!getAllPatientRes?.totalPatient?.length || !pathName?.split('/')?.[1]) {
      return;
    }
    if (pathName?.split('/')?.[1] !== 'patients') {
      dispatch(getAllPatientsListResetAction());
      return;
    }
  }, [pathName]);

  const currentPath = window?.location?.pathname?.split('/').filter(Boolean)?.pop();

  return (
    <Routes>
      <Route
        path={`${index}`}
        element={
          <PrivateRoute>
            <DashboardLayout>
              <ErrorBoundary
                fallback={<ErrorFallback />}
                key={
                  currentPath === 'error' || currentPath === 'document-error'
                    ? location.pathname
                    : null
                }
              >
                <Suspense>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </DashboardLayout>
          </PrivateRoute>
        }
      >
        <Route
          path="edit-medical-codes"
          element={<MedicalCodeEditor />}
        />
        <Route
          path={checkHost === 'localhost' || checkHost === '127.0.0.1' ? '/:subdomain?' : '/'}
          element={<RedirectTab />}
        />

        <Route
          path={`${landingPage}/:realm?`}
          element={<LandingPage />}
        />

        <Route
          path={`/server-error`}
          element={<ServerErrorPage />}
        />

        <Route
          path={redirectSupervisor}
          element={<RedirectTab />}
        />

        {has(Permissions.READ_BOARD_SELF) && (
          <Route
            path={dashboard}
            element={<Dashboard />}
          />
        )}

        {has(Permissions.READ_BOARD) && (
          <Route
            path={departmentComplianceAlerts}
            element={<DepartmentDashboard />}
          />
        )}

        {has(Permissions.READ_BOARD) && (
          <Route
            path={departmentMetrics}
            element={<DepartmentMetrics />}
          />
        )}

        {/* RA-2133: MDS Metrics — same layout/chrome as Department Metrics, own permission. */}
        {has(Permissions.READ_MDS_COORDINATOR_BOARD) && (
          <Route
            path={mdsMetrics}
            element={<MdsMetrics />}
          />
        )}

        {has(Permissions.READ_BOARD) && (
          <Route
            path={departmentDownloads}
            element={<DepartmentDownloads />}
          />
        )}

        {/* BRK-1850: flag + permission enforced inside MdsDashboardGuard */}
        <Route
          path={paths.mdsDashboard}
          element={<MdsDashboard />}
        />
        <Route
          path={`${paths.mdsDashboard}/:patientId`}
          element={<MdsDashboardPatient />}
        />

        <Route
          path={physicianDashboard}
          element={<PhysicianDashboard />}
        />
        <Route
          path={physicianDocumentsSign}
          element={<PhysicianDocumentsSign />}
        />
        <Route
          path={'/error'}
          element={<ErrorPage />}
        />

        <Route
          path={'/document-error'}
          element={<DocumentErrorPage />}
        />

        {has(Permissions.READ_MONTH_END_ALERT) && (
          <Route
            path={monthDayAlert}
            element={<MonthDayAlert />}
          />
        )}

        {has(Permissions.READ_MONTH_END_ALERT) && (
          <Route
            path={monthlyCloseoutAlert}
            element={<MonthlyCloseoutAlert />}
          />
        )}

        {has(Permissions.READ_MONTH_END_ALERT) && (
          <Route
            path={preliminaryInvoiceReview}
            element={<BackToPeriodRedirect />}
          />
        )}

        {has(Permissions.READ_MONTH_END_ALERT) && (
          <Route
            /*  Wildcard: the review is now the rebill plugin, which routes its own :monthKey
                segment (BOPS-131). */
            path={`${preliminaryInvoiceReview}/*`}
            element={<PreliminaryInvoiceReview />}
          />
        )}

        {has(Permissions.READ_DAILY_NOTES) && (
          <Route
            path={dailySessionNotes}
            element={<DailySessionNotes />}
          />
        )}

        {/* THIN-7866: /physician-dashboard/doc-sign rendered the Jotform iframe with no toggle
            check at all. The v1 physician table now sends physicians to the Reform physician
            sign screen (paths.physicianDocumentsSign), so the route is gone rather than
            repointed — the therapist Reform screen signs via /sign-document, which cannot
            express a PHYSICIAN signer. */}
        <Route
          path={`${dashboard}/doc-sign`}
          element={<DashboardDocSignaturePreviewNew />}
        />

        <Route
          path={`${dashboard}/doc-preview-carousel`}
          element={<DocumentPreviewCarousel />}
        />
        <Route
          path={`/dashboard/doc`}
          element={<TherapistDashboardDocPreview />}
        />
        <Route
          path={`/physician-dashboard/doc`}
          element={<TherapistDashboardDocPreview />}
        />
        <Route
          path="/document/document-edit"
          element={<TherapistDashboardDocEdit />}
        />

        {has(Permissions.READ_PATIENT) && (
          <>
            <Route
              path={patients}
              element={<Patients />}
            />
            <Route
              path={`${patients}/${createPatient}`}
              element={<AddPatientForm action={'Create'} />}
            />
            <Route
              path={`${patients}${editPatient}`}
              element={<AddPatientForm action={'Edit'} />}
            />

            <Route
              path={`${patients}/upload`}
              element={<PatientUpload />}
            />
            <Route
              path={`${patients}/recent-uploaded`}
              element={<RecentUploadedfiles />}
            />
            {has(Permissions.READ_SCREENING) && (
              <>
                <Route
                  path={`${patients}/${patientDetails}${screening}${addNewScreening}`}
                  element={<AddNewScreening />}
                />
                <Route
                  path={`${patients}/${patientDetails}${screening}/view-form`}
                  element={<ViewAddNewScreenForm />}
                />
                <Route
                  path={`${patients}/${patientDetails}${screening}/view-document`}
                  element={<ScreeningPreviewDocument />}
                />
                <Route
                  path={`${patients}/${patientDetails}${screening}/view-edit-document`}
                  element={<EditScreeningDocumentPreview />}
                />
              </>
            )}
            {has(Permissions.READ_EOC) && (
              <>
                <Route
                  path={`${patients}/${patientDetails}${episodeOfCare}${addNewEpisodeOfCare}`}
                  element={<AddNewEpisodeOfCare action="Create" />}
                />
                <Route
                  path={`${patients}/${patientDetails}${episodeOfCare}${editEpisodeOfCare}`}
                  element={<AddNewEpisodeOfCare action="Edit" />}
                />
                <Route
                  path={`${patients}/${patientDetails}${episodeOfCare}${details}`}
                  element={<EpisodeOfCareViewDetails />}
                />
              </>
            )}
            <Route
              path={`${patients}/${patientDetails}${mdsAssessments}${createMdsAssessment}`}
              element={<TherapyPlanningEditDetails />}
            />
          </>
        )}

        {/* {has(Permissions.READ_AVAILABILITY) && ( */}
        <Route path={laborManagement.index}>
          {/* The sidebar's Labor Management entry links at the section root, which matched
              no route and fell through to 404. Send it on to the first screen the user can
              actually open. */}
          <Route
            index
            element={
              <Navigate
                replace
                to={
                  hasLaborManagementAccess(has)
                    ? laborManagement.laborHoursManagement
                    : laborManagement.myAvailability
                }
              />
            }
          />
          {has(Permissions.READ_AVAILABILITY) && (
            <Route
              path={laborManagement.myAvailability}
              element={<MyAvailability />}
            />
          )}
          {/* Same rule as the sidebar (see hasLaborManagementAccess) — gating these on the
              umbrella LABOR_MANAGEMENT alone 404'd them for roles that only hold the
              granular labor permissions. */}
          {hasLaborManagementAccess(has) && (
            <Route
              path={laborManagement.laborHoursManagement}
              element={<LaborHourManagement />}
            />
          )}
          <Route
            path={laborManagement.npcLog}
            element={<NpcLog />}
          />
          {/* THIN-8096: the approval surface is its own permission pair — labor-management
              access must not register this route, or a therapist holding only the legacy
              Create/Update Labor Management permissions could reach a screen whose data the
              backend then refuses. Same rule as the sidebar entry. */}
          {hasLaborValidationViewAccess(has) && (
            <Route
              path={laborManagement.laborReportAndApproval}
              element={<LaborReportAndApproval />}
            />
          )}
        </Route>

        {has(Permissions.READ_DAILY_TASK) && (
          <Route path={schedule.index}>
            <Route
              path={schedule.therapistSchedule}
              element={<TherapistScheduleNewUI />}
            />
          </Route>
        )}

        <Route
          path={`${patients}/${patientDetails}${episodeOfCare}${details}${previewDocument}`}
          element={<PreviewDocument />}
        />
        <Route
          path={`${patients}/${patientDetails}${episodeOfCare}${details}${editDocument}`}
          element={<EditDocumentPreview />}
        />
        <Route
          path={`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`}
          element={<CreateDocumentPrview />}
        />
        <Route
          path={`${patients}/${patientDetails}${episodeOfCare}${details}${physicianSign}`}
          element={<PhysicianSignDocument />}
        />
        <Route
          path={`${patients}/${patientDetails}${episodeOfCare}${details}${getSignaturePreview}`}
          element={<SignatureTemplatePreviewNew />}
        />

        <Route
          path={`${patients}/${deactivate}`}
          element={<DeleteModal />}
        ></Route>
        {has(Permissions.READ_PATIENT) && (
          <>
            <Route
              path={`${patients}/${facilityStay}/${addFacilityStay}`}
              element={<EditFacilityStayPage />}
            ></Route>

            <Route
              path={`${patients}/${patientDetails}${screening}${sreeningDetails}`}
              element={<ScreeningDetails />}
            />
            <Route
              path={`${patients}/${patientDetails}`}
              element={<PatientDetails />}
            >
              {has(Permissions.READ_SCREENING) && (
                <Route
                  path={`${patients}/${patientDetails}${screening}`}
                  element={<PatientScreening />}
                ></Route>
              )}
              {has(Permissions.READ_EOC) && (
                <Route
                  path={`${patients}/${patientDetails}${episodeOfCare}`}
                  element={<PatientEpisodeOfcare />}
                />
              )}
              <Route
                path={`${patients}/${patientDetails}${payers}`}
                element={<PatientPayersInfoTab />}
              />
              <Route
                path={`${patients}/${patientDetails}${mdsAssessments}`}
                element={<MdsAssessments />}
              />
            </Route>
            <Route
              path={`${patients}/${patientDetails}${payers}${addNewPayer}`}
              element={<AddEditPatientPayer />}
            />
          </>
        )}

        <Route
          path={`${patients}/${patientDetails}${editFacilityStay}`}
          element={<EditFacilityStayPage />}
        />
        <Route
          path={`${'/scheduling/therapy-planning'}/upload`}
          element={<MdsAssessmentUpload />}
        />

        {/* old code of therapy planning */}

        {/* {has(Permissions.READ_THERAPY_PLANNING) && (
          <>
           
            <Route
              path={`${therapyPlanning.index}${therapyPlanning.details}`}
              element={<TherapyPlanningDetails />}
            >
            
              <Route
                index
                element={<MDSAssessment />}
              />
            
              <Route
                path={`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.info}`}
                element={<TherapyPlanningInfo />}
              />
              
              <Route
                path={`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.authorization}`}
                element={<AuthorizationDetails />}
              />
            </Route>
            <Route
              path={`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.editAuthorization}`}
              element={<AuthorizationEditDetails />}
            />

            <Route
              path={`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.editDetails}`}
              element={<TherapyPlanningEditDetails />}
            />
          </>
        )} */}

        {/* new code of therapy planning 2.0 */}

        {has(Permissions.READ_THERAPY_PLANNING) && (
          <>
            <Route
              path={`${therapyPlanning.index}${therapyPlanning.details}`}
              element={<TherapyPlanningDetails_2_0 />}
            >
              <Route
                index
                element={<MDSAssessment />}
              />

              <Route
                path={`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.info}`}
                element={<TherapyPlanningInfo_2_0 />}
              />
              <Route
                path={`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.authorization}`}
                element={<AuthorizationDetails />}
              />
            </Route>

            <Route
              path={`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.editAuthorization}`}
              element={<AuthorizationEditDetails />}
            />

            <Route
              path={`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.editDetails}`}
              element={<TherapyPlanningEditDetails />}
            />
          </>
        )}
        <Route
          path="*"
          element={<NotPermission />}
        />
      </Route>

      {/* post reset password */}

      <Route
        path={index}
        element={
          <PrivateRoute>
            <AuthLayout>
              <Suspense>
                <Outlet />
              </Suspense>
            </AuthLayout>
            {/* APIT-622 — the only authenticated route outside DashboardLayout, so the
                lockout blocker has to be mounted here too or it can be reached by URL. */}
            <EmploymentLockoutModal />
          </PrivateRoute>
        }
      >
        <Route
          path="/post-auth-forgot-password"
          element={<PostResetPassword />}
        />
        {/* APIT-696 — lands here from the emailed magic link with a fresh Keycloak session.
            Authenticated like post-auth-forgot-password above, for the same reason: it needs
            to be reachable outside DashboardLayout's permission-gated routes. */}
        <Route
          path={paths.passwordResetComplete}
          element={<PasswordResetComplete />}
        />
      </Route>

      {/* Public Routes */}
      <Route
        path={'auth'}
        element={
          <PublicRoute>
            <AuthLayout>
              <Suspense>
                <Outlet />
              </Suspense>
            </AuthLayout>
          </PublicRoute>
        }
      >
        <Route
          path="login"
          element={<Login />}
        />
        {/* forgot-password is NOT registered here: app.tsx renders it before this Router ever
            mounts, so a signed-out visitor never hits the auth gate (APIT-696, mirrors the
            BOPS-182 /invoice-download exemption). See src/pages/auth/forgot-password.tsx. */}
        <Route
          path="reset-password"
          element={<ResetPassword />}
        />
        <Route
          path={landingPageLogout}
          element={<LogoutLanding />}
        />
      </Route>
    </Routes>
  );
}

export default Router;
